import {
  type CellClassParams,
  type ColDef,
  type ICellRendererParams,
  type RowClassParams,
  type RowClassRules,
  type ValueGetterParams,
} from 'ag-grid-community';
import { compact, isNil, noop } from 'lodash';
import every from 'lodash/every';
import { useMemo, useState } from 'react';
import type { Fund, Portfolio, PortfolioNodeDesignationEnum } from 'venn-api';
import { GetColor, getHelpCenterLinks } from 'venn-ui-kit';
import { assertNotNil, useHasFF } from 'venn-utils';
import { HeaderRendererWithLink } from '../../../studio-blocks/components/grid/renderers/HeaderRendererWithLink';
import type { MappedPortfolioNodeCellRendererProps } from '../../../studio-blocks/components/grid/renderers/mapped-portfolio-node-cell-renderer/types';
import {
  ALLOCATION_CELL_RENDERER,
  MAPPED_HISTORICAL_PORTFOLIO_NODE_CELL_RENDERER,
  MAPPED_PRO_FORMA_PORTFOLIO_NODE_CELL_RENDERER,
  STRATEGY_ASSIGNMENT_CELL_RENDERER,
} from '../../../studio-blocks/customAnalysisContants';
import type { PortfolioAccessMode, RowData } from '../types';
import MutliPortfolioActionsCell from './cells/MutliPortfolioActionsCell';
import type { HistoricalFundRangeAnalysis } from './components/specialized/MultiHistoricalPortfolioContext';
import { NameWithMappingErrorRenderer } from './mapping/NameWithMappingErrorRenderer';
import {
  getAllFundIds,
  historicalNodeHasShortHistory,
  historicalNodeIsDuplicated,
  nodeNeedsMapping,
  serializePath,
  CASH_BORROWING,
  CASH_HOLDING,
  isPortfolioNeedAssigning,
} from './review/helpers';
import { useIsLeverageShortEnabled } from '../../../hooks/leverageAndShort';
import { formatLotName, getLotMarketValue } from '../utils';

export const useUploadPortfolioAllocatorColumnDefs = ({
  portfolio,
  excludedInvestments,
  onApplyMapping,
  isHistorical,
  includeInvestment,
  excludeInvestment,
  changeInvestmentDesignation,
}: {
  portfolio: Portfolio;
  excludedInvestments: Set<string>;
  onApplyMapping: (path: number[], fund: Fund) => void | Promise<void>;
  isHistorical: boolean;
  includeInvestment: (path: number[]) => void;
  excludeInvestment: (path: number[]) => void;
  changeInvestmentDesignation: (path: number[], designation: PortfolioNodeDesignationEnum) => void;
}): ColDef<RowData<Portfolio>>[] => {
  const hasHistoricals = useHasFF('historical_portfolios_ff');
  const isLeverageShortEnabled = useIsLeverageShortEnabled();
  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');
  const [isPathRemapped, setIsPathRemapped] = useState<Record<string, boolean>>({});
  const [isFundRemapped, setIsFundRemapped] = useState<Record<string, boolean>>({});
  const allFundIdsInPortfolio = getAllFundIds(portfolio);

  const isFundIdDisabledForMapping = (fundId: string) => {
    if (isHistorical) {
      // For historical portfolio we allow remapping to any fund even if it creates duplicates
      // User can then resolve duplicates by excluding investments if they wish
      return false;
    }
    // For pro-forma portfolio we don't allow any duplicate funds in the entire portfolio
    return allFundIdsInPortfolio.includes(fundId);
  };

  const columns: ColDef<RowData<Portfolio>>[] = compact([
    hasManualStrategyAssignment && {
      headerName: 'Assign strategy',
      sortable: false,
      cellRenderer: STRATEGY_ASSIGNMENT_CELL_RENDERER,
      cellRendererParams: (params: ICellRendererParams<RowData<Portfolio>>) => {
        const { isFund } = nodeNeedsMapping(params.data!, excludedInvestments);
        const fund = params.data?.node?.fund;

        const isExcluded = params.data?.path ? excludedInvestments.has(serializePath(params.data.path).value) : false;

        if (
          !isFund ||
          !fund ||
          isExcluded ||
          params.data?.isLotRow ||
          nodeNeedsMapping(params.data!, excludedInvestments).needsMapping
        ) {
          return { showAssignButton: false };
        }

        return {
          showAssignButton: true,
          fund,
          path: params.data!.path,
        };
      },
      colId: 'assigned',
      flex: 1,
      cellClass: 'overflow-visible',
      minWidth: 110,
    },
    {
      headerName: 'Map investment',
      sortable: false,
      cellRenderer: isHistorical
        ? MAPPED_HISTORICAL_PORTFOLIO_NODE_CELL_RENDERER
        : MAPPED_PRO_FORMA_PORTFOLIO_NODE_CELL_RENDERER,
      cellRendererParams: (
        params: ICellRendererParams<RowData<Portfolio>>,
      ): Partial<MappedPortfolioNodeCellRendererProps> => {
        if (params.data?.isLotRow) {
          return {
            allowRemapping: false,
            needsMapping: false,
            onApplyMapping: noop,
            isFundIdDisabledForMapping: () => true,
            isPathRemapped: {},
            isFundRemapped: {},
          };
        }
        const { isFund, needsMapping } = nodeNeedsMapping(params.data!, excludedInvestments);
        const onApplyMappingWrapper = (path: number[], fund: Fund) => {
          onApplyMapping(path, fund);
          setIsPathRemapped((isPathRemapped) => ({
            ...isPathRemapped,
            [serializePath(path).value]: true,
          }));

          if (fund.id) {
            setIsFundRemapped((isFundRemapped) => ({
              ...isFundRemapped,
              [fund.id]: true,
            }));
          }
        };

        return {
          needsMapping,
          onApplyMapping: onApplyMappingWrapper,
          isFundIdDisabledForMapping,
          isPathRemapped,
          isFundRemapped,
          allowRemapping:
            isFund &&
            !excludedInvestments.has(serializePath(assertNotNil(params.data!.path)).value) &&
            params.data!.node.designation !== 'CASH',
        };
      },
      colId: 'mapped',
      flex: 1,
      cellClass: 'overflow-visible',
      minWidth: 115,
    },
    {
      headerName: 'Allocation',
      headerClass: ['ag-right-aligned-header'],
      cellRenderer: ALLOCATION_CELL_RENDERER,
      sortable: false,
      cellStyle: { paddingRight: 3 },
      cellRendererParams: (params: ICellRendererParams) => {
        const { allocation, fund, netAssets } = params.data.node;
        const value = params.data?.isLotRow || !isLeverageShortEnabled ? allocation : netAssets;
        return {
          value,
          originalValue: value,
          isRoot: params.node.level === 0,
          isStrategy: isNil(fund),
          onUpdateAllocation: noop,
          isExcluded: excludedInvestments.has(serializePath(assertNotNil(params.data.path)).value),
          isFillForward: params.data.isFillForward,
          isMultiPortfolioUploader: true,
          isLotRow: Boolean(params.data.isLotRow),
        };
      },
      colId: 'allocation',
      flex: 1,
      minWidth: 130,
    },
    hasHistoricals
      ? {
          headerName: '',
          cellRenderer: MutliPortfolioActionsCell,
          cellRendererParams: ({ data }: ICellRendererParams<RowData<Portfolio>>) => {
            if (data?.isLotRow) {
              return {
                isHistorical,
                isExcluded: false,
                includeInvestment: noop,
                excludeInvestment: noop,
                changeInvestmentDesignation: noop,
              };
            }
            const isFund = !isNil(data?.node?.fund) && !isNil(data?.path);

            const includeThisInvestment = () => {
              includeInvestment(assertNotNil(data?.path));
            };

            const excludeThisInvestment = () => {
              excludeInvestment(assertNotNil(data?.path));
            };

            return {
              isHistorical,
              isFund,
              isExcluded: excludedInvestments.has(serializePath(assertNotNil(data?.path)).value),
              includeInvestment: includeThisInvestment,
              excludeInvestment: excludeThisInvestment,
              changeInvestmentDesignation,
            };
          },
          sortable: false,
          colId: 'actions',
          minWidth: 70,
          maxWidth: 70,
        }
      : null,
  ]);

  return columns;
};

export const getRowData = (
  node: Portfolio,
  selectedDate?: Date,
  hasTaxImpact: boolean = false,
): RowData<Portfolio>[] => {
  return getRowDataImpl(node, node, 0, selectedDate, hasTaxImpact).filter((row) => !row.isHidden);
};

const getRowDataImpl = (
  node: Portfolio,
  root: Portfolio,
  nodeId: number,
  selectedDate?: Date,
  hasTaxImpact: boolean = false,
): RowData<Portfolio>[] => {
  if (node.fund) {
    const fundRow: RowData<Portfolio> = {
      path: [nodeId],
      node,
      root,
      isHidden: !node.allocation && !node.cashBalance && !node.netAssets,
      isFillForward:
        node.fund && selectedDate !== undefined
          ? node?.closingAllocationsTs?.[0]?.[0] !== selectedDate.valueOf()
          : false,
    };

    if (node.lots && node.lots.length > 1 && hasTaxImpact) {
      const lotRows: RowData<Portfolio>[] = node.lots.map((lot, lotIndex) => {
        const lotMarketValue = getLotMarketValue(node, lot);
        return {
          path: [nodeId, lotIndex],
          node: {
            ...node,
            allocation: lotMarketValue,
            netAssets: lotMarketValue,
            name: formatLotName(lot),
          } as Portfolio,
          root,
          isHidden: false,
          isFillForward: false,
          isLotRow: true,
        };
      });

      return [fundRow, ...lotRows];
    }

    return [fundRow];
  }

  const descendantsRowData = node.children.map((child, childIndex) => {
    const childData = getRowDataImpl(child, root, childIndex, selectedDate, hasTaxImpact);
    return childData.map((row) => {
      return {
        ...row,
        path: [nodeId, ...row.path],
      };
    });
  });

  const directChildrenRowData = descendantsRowData.map((childData) => childData[0]!);

  const nodeRowData = {
    path: [nodeId],
    node,
    root,
    // Root node (representing entire portfolio) should not be hidden even if all of its children are hidden
    isHidden: node !== root && every(directChildrenRowData, (child) => child.isHidden),
    isFillForward: false,
  };

  return [nodeRowData, ...descendantsRowData.flat()];
};

const portfolioAllocatorStylingProps = {
  root: {
    fontWeight: 700,
    fontSize: 16,
  },
  strategy: {
    fontWeight: 700,
    fontSize: 14,
  },
  investment: {
    fontWeight: 400,
    fontSize: 14,
  },
};

const OVERWRITE_TOOLTIP_MSG =
  'Replaces all investments, strategies, strategy hierarchy, and allocations from the existing portfolio in the Data Library with the information below.';
const CREATE_NEW_TOOLTIP_MSG =
  'Creates a new portfolio with all investments, strategies, strategy hierarchy, and allocations in the Data Library with the information below.';

const useCommonAutoGroupColumnDefs = (accessMode: PortfolioAccessMode) => ({
  flex: 5,
  minWidth: 200,
  wrapText: true,
  autoHeight: true,
  headerComponent: HeaderRendererWithLink,
  headerComponentParams: {
    displayName: accessMode === 'OVERWRITE_EXISTING' ? 'Overwrite entire portfolio' : 'Create new portfolio',
    linkTo: getHelpCenterLinks().MULTI_PORTFOLIO_UPLOADER_FAQ_HREF,
    tooltip: accessMode === 'OVERWRITE_EXISTING' ? OVERWRITE_TOOLTIP_MSG : CREATE_NEW_TOOLTIP_MSG,
    iconColor: GetColor.Black,
  },
  sortable: false,
  field: 'path' as const,
  valueGetter: (params: ValueGetterParams<RowData<Portfolio>>) => params.data?.node.name,
  cellStyle: (params: CellClassParams<RowData<Portfolio>>) => {
    const { level, data } = params.node;
    const baseStyle =
      level === 0
        ? portfolioAllocatorStylingProps.root
        : isNil(data?.node?.fund)
          ? portfolioAllocatorStylingProps.strategy
          : portfolioAllocatorStylingProps.investment;

    return {
      ...baseStyle,
      paddingLeft: 0,
    };
  },
  colId: 'name',
  cellRenderer: 'agGroupCellRenderer',
});

const useProFormaAutoGroupColumnDefs = ({
  accessMode,
  excludedInvestments,
  strategyData,
}: {
  accessMode: PortfolioAccessMode;
  excludedInvestments: Set<string>;
  strategyData: {
    hasManualStrategyAssignment: boolean;
  };
}) => ({
  ...useCommonAutoGroupColumnDefs(accessMode),
  cellRendererParams: (params: ICellRendererParams<RowData<Portfolio>>) => {
    const portfolio = params.data?.node;

    const mapping = nodeNeedsMapping(assertNotNil(params?.data), excludedInvestments);
    const isAssigned = Boolean(params.data?.path && params.data.path.length > 2);

    const needsAssigning =
      isPortfolioNeedAssigning(portfolio, params.data?.path, excludedInvestments, strategyData) && !isAssigned;

    return {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      innerRenderer: NameWithMappingErrorRenderer,
      innerRendererParams: {
        name:
          portfolio?.designation === 'CASH'
            ? (portfolio?.netAssets ?? 0) < 0
              ? CASH_BORROWING
              : CASH_HOLDING
            : portfolio?.name,
        isExcluded: excludedInvestments.has(serializePath(assertNotNil(params?.data?.path)).value),
        needsMapping: mapping.needsMapping,
        duplicateInvestment: false,
        isCashNode: portfolio?.designation === 'CASH',
        cashBalance: portfolio?.cashBalance ?? 0,
        needsAssigning,
      },
    };
  },
});

const useHistoricalAutoGroupColumnDefs = ({
  accessMode,
  selectedDate,
  excludedInvestments,
  rangeAnalysis,
  strategyData,
}: {
  accessMode: PortfolioAccessMode;
  selectedDate: Date;
  excludedInvestments: Set<string>;
  rangeAnalysis: HistoricalFundRangeAnalysis;
  strategyData?: {
    hasManualStrategyAssignment: boolean;
  };
}) => ({
  ...useCommonAutoGroupColumnDefs(accessMode),
  cellRendererParams: (params: ICellRendererParams<RowData<Portfolio>>) => {
    const data = assertNotNil(params.data);
    const portfolio = data.node;
    const fundId = portfolio.fund?.id;

    const isExcluded = data.path ? excludedInvestments.has(serializePath(data.path).value) : false;

    const mapping = nodeNeedsMapping(data, excludedInvestments);
    const isAssigned = Boolean(data.path && data.path.length > 2);

    const needsAssigning =
      strategyData?.hasManualStrategyAssignment && fundId && !isExcluded && !mapping.needsMapping && !isAssigned;

    return {
      suppressCount: true,
      suppressDoubleClickExpand: true,
      innerRenderer: NameWithMappingErrorRenderer,
      innerRendererParams: {
        name: portfolio.name,
        isExcluded: excludedInvestments.has(serializePath(assertNotNil(params?.data?.path)).value),
        needsMapping: mapping.needsMapping,
        duplicateInvestment: historicalNodeIsDuplicated(data, selectedDate.getTime(), excludedInvestments)
          .duplicateInvestment,
        shortHistory:
          !isNil(fundId) && historicalNodeHasShortHistory(portfolio.closingAllocationsTs, rangeAnalysis[fundId]),
        needsAssigning,
      },
    };
  },
});

const useRowClassRules = ({
  excludedInvestments,
  rangeAnalysis,
  selectedDate,
  strategyData,
}: {
  excludedInvestments: Set<string>;
  rangeAnalysis?: HistoricalFundRangeAnalysis;
  selectedDate?: Date;
  strategyData?: {
    hasManualStrategyAssignment: boolean;
  };
}) => {
  return useMemo<RowClassRules<RowData<Portfolio>>>(() => {
    const isErrorRow = (params: RowClassParams) => {
      if (!params.data) return false;

      const isExcluded = params.data?.path ? excludedInvestments.has(serializePath(params.data.path).value) : false;
      if (isExcluded) return false;

      const mappingStatus = nodeNeedsMapping(params.data, excludedInvestments);
      const duplicateStatus = historicalNodeIsDuplicated(params.data, selectedDate?.getTime(), excludedInvestments);

      if (mappingStatus.needsMapping || duplicateStatus.duplicateInvestment) {
        return true;
      }

      if (strategyData?.hasManualStrategyAssignment && mappingStatus.isFund && params.data.node?.fund?.id) {
        const isAssigned = Boolean(params.data.path && params.data.path.length > 2);
        if (!isAssigned) {
          return true;
        }
      }

      return false;
    };

    return {
      // row style function creating a CSS class to style the background of the row
      'error-row': (params) => {
        return isErrorRow(params);
      },
      'warning-row': (params) => {
        const { node, path } = params.data ?? {};
        if (excludedInvestments.has(serializePath(assertNotNil(path)).value)) {
          return false;
        }
        const isError = isErrorRow(params);
        const fundId = node?.fund?.id;
        return (
          !isError &&
          !isNil(fundId) &&
          historicalNodeHasShortHistory(node?.closingAllocationsTs, rangeAnalysis?.[fundId])
        );
      },
      'lot-row': (params) => Boolean(params.data?.isLotRow),
    };
  }, [excludedInvestments, rangeAnalysis, selectedDate, strategyData]);
};

type SharedAllocatorProps = {
  portfolio: Portfolio;
  excludedInvestments: Set<string>;
  accessMode: PortfolioAccessMode;
  applyInvestmentRemap: (path: number[], fund: Fund) => void | Promise<void>;
  excludeInvestment: (path: number[]) => void;
  includeInvestment: (path: number[]) => void;
  changeInvestmentDesignation: (path: number[], designation: PortfolioNodeDesignationEnum) => void;
  strategyData: {
    hasManualStrategyAssignment: boolean;
  };
};

type ProFormaAllocatorProps = SharedAllocatorProps;

type HistoricalAllocatorProps = SharedAllocatorProps & {
  selectedDate: Date;
  rangeAnalysis: HistoricalFundRangeAnalysis;
};

export const useUploadProFormaPortfolioAllocator = ({
  portfolio,
  excludedInvestments,
  accessMode,
  applyInvestmentRemap,
  includeInvestment,
  excludeInvestment,
  changeInvestmentDesignation,
  strategyData,
}: ProFormaAllocatorProps) => {
  const hasTaxImpact = useHasFF('tax_impact_ff');

  return {
    rowClassRules: useRowClassRules({ excludedInvestments, strategyData }),
    autoGroupColumnDefs: useProFormaAutoGroupColumnDefs({
      accessMode,
      excludedInvestments,
      strategyData,
    }),
    columnDefs: useUploadPortfolioAllocatorColumnDefs({
      portfolio,
      excludedInvestments,
      onApplyMapping: applyInvestmentRemap,
      isHistorical: false,
      includeInvestment,
      excludeInvestment,
      changeInvestmentDesignation,
    }),
    rowData: getRowData(portfolio, undefined, hasTaxImpact),
  };
};

export const useUploadHistoricalPortfolioAllocator = ({
  portfolio,
  excludedInvestments,
  accessMode,
  applyInvestmentRemap,
  selectedDate,
  includeInvestment,
  excludeInvestment,
  changeInvestmentDesignation,
  rangeAnalysis,
  strategyData,
}: HistoricalAllocatorProps) => {
  const hasTaxImpact = useHasFF('tax_impact_ff');

  return {
    rowClassRules: useRowClassRules({
      excludedInvestments,
      rangeAnalysis,
      selectedDate,
      strategyData,
    }),
    autoGroupColumnDefs: useHistoricalAutoGroupColumnDefs({
      accessMode,
      excludedInvestments,
      selectedDate,
      rangeAnalysis,
      strategyData,
    }),
    columnDefs: useUploadPortfolioAllocatorColumnDefs({
      portfolio,
      excludedInvestments,
      onApplyMapping: applyInvestmentRemap,
      isHistorical: true,
      includeInvestment,
      excludeInvestment,
      changeInvestmentDesignation,
    }),
    rowData: getRowData(portfolio, selectedDate, hasTaxImpact),
  };
};
